/**
 * Voxel terrain: one InstancedMesh per tile type, one instance per column.
 * A column is a unit-wide box scaled to its height; the top face gets the tile
 * colour and the sides a slightly deeper tone so cubes read as cubes.
 *
 * The world is mutable, so every mesh is allocated for the whole grid and the
 * live instances are kept packed at the front. Changing a cell rewrites one
 * instance (same tile) or moves it between meshes (swapping the last instance
 * into the hole it leaves) — never a rebuild.
 */
import * as THREE from 'three';
import type { Tile, WorldSnapshot } from '@village/shared';
import { WATER_LEVEL, idx } from '@village/shared';
import { TILE_COLORS, TILE_SIDE_COLORS } from '../palette';

/** world units per height step */
export const Y_STEP = 0.5;

export const TILE_ORDER: Tile[] = ['grass', 'dirt', 'sand', 'stone', 'path', 'crop', 'plank', 'cobble', 'water'];

function columnMaterials(tile: Tile): THREE.Material[] {
  const wet = tile === 'water';
  const top = new THREE.MeshStandardMaterial({
    color: new THREE.Color(TILE_COLORS[tile]),
    flatShading: true,
    roughness: wet ? 0.35 : 0.92,
    metalness: 0,
    transparent: wet,
    opacity: wet ? 0.88 : 1,
  });
  const side = new THREE.MeshStandardMaterial({
    color: new THREE.Color(TILE_SIDE_COLORS[tile]),
    flatShading: true,
    roughness: wet ? 0.45 : 0.95,
    metalness: 0,
    transparent: wet,
    opacity: wet ? 0.88 : 1,
  });
  // BoxGeometry groups: +x, -x, +y, -y, +z, -z
  return [side, side, top, side, side, side];
}

/** Deterministic per-cell brightness so flat areas don't band. */
function jitterOf(cell: number): number {
  return 0.97 + ((Math.sin(cell * 12.9898) * 43758.5453) % 1) * 0.06;
}

export class Terrain {
  readonly group = new THREE.Group();
  readonly size: number;
  readonly heights: number[];
  readonly tiles: Tile[];

  private geom = new THREE.BoxGeometry(1, 1, 1);
  private meshes = new Map<Tile, THREE.InstancedMesh>();
  /** cell -> slot inside the mesh of its current tile */
  private slotOfCell: Int32Array;
  /** tile -> slot -> cell */
  private cellOfSlot = new Map<Tile, Int32Array>();
  private counts = new Map<Tile, number>();
  private dummy = new THREE.Object3D();
  private color = new THREE.Color();

  constructor(world: WorldSnapshot) {
    this.group.name = 'terrain';
    this.size = world.size;
    this.heights = [...world.heights];
    this.tiles = [...world.tiles];
    const cells = this.size * this.size;
    this.slotOfCell = new Int32Array(cells).fill(-1);

    for (const tile of TILE_ORDER) {
      const mesh = new THREE.InstancedMesh(this.geom, columnMaterials(tile), cells);
      mesh.name = `terrain:${tile}`;
      mesh.castShadow = tile !== 'water';
      mesh.receiveShadow = true;
      mesh.frustumCulled = false; // the grid is always on screen; count changes constantly
      mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cells * 3), 3);
      mesh.count = 0;
      this.meshes.set(tile, mesh);
      this.cellOfSlot.set(tile, new Int32Array(cells).fill(-1));
      this.counts.set(tile, 0);
      this.group.add(mesh);
    }

    for (let cell = 0; cell < cells; cell++) this.insert(cell, this.tiles[cell]);
    for (const tile of TILE_ORDER) this.markDirty(tile);
    this.flush();
  }

  // ------------------------------------------------------------- reads

  heightAt(x: number, z: number): number {
    const cx = Math.max(0, Math.min(this.size - 1, Math.round(x)));
    const cz = Math.max(0, Math.min(this.size - 1, Math.round(z)));
    return this.heights[idx(this.size, cx, cz)] ?? 1;
  }

  tileAt(x: number, z: number): Tile {
    const cx = Math.max(0, Math.min(this.size - 1, Math.round(x)));
    const cz = Math.max(0, Math.min(this.size - 1, Math.round(z)));
    return this.tiles[idx(this.size, cx, cz)] ?? 'grass';
  }

  /** Top surface of the column at (x, z), in world units. */
  surfaceY(x: number, z: number): number {
    return Math.max(this.heightAt(x, z), WATER_LEVEL) * Y_STEP;
  }

  // ------------------------------------------------------------- writes

  /** Apply one `WorldDiff` cell. */
  setCell(cell: number, height: number, tile: Tile): void {
    if (cell < 0 || cell >= this.tiles.length) return;
    const prevTile = this.tiles[cell];
    this.heights[cell] = height;
    if (prevTile === tile) {
      this.write(tile, this.slotOfCell[cell], cell);
      this.markDirty(tile);
      return;
    }
    this.remove(cell, prevTile);
    this.tiles[cell] = tile;
    this.insert(cell, tile);
    this.markDirty(prevTile);
    this.markDirty(tile);
  }

  private insert(cell: number, tile: Tile): void {
    const mesh = this.meshes.get(tile)!;
    const slot = this.counts.get(tile)!;
    this.counts.set(tile, slot + 1);
    mesh.count = slot + 1;
    this.cellOfSlot.get(tile)![slot] = cell;
    this.slotOfCell[cell] = slot;
    this.write(tile, slot, cell);
  }

  private remove(cell: number, tile: Tile): void {
    const mesh = this.meshes.get(tile)!;
    const slot = this.slotOfCell[cell];
    if (slot < 0) return;
    const last = this.counts.get(tile)! - 1;
    const map = this.cellOfSlot.get(tile)!;
    if (slot !== last) {
      // swap the last live instance into the hole
      const movedCell = map[last];
      map[slot] = movedCell;
      this.slotOfCell[movedCell] = slot;
      this.write(tile, slot, movedCell);
    }
    map[last] = -1;
    this.counts.set(tile, last);
    mesh.count = last;
    this.slotOfCell[cell] = -1;
  }

  private write(tile: Tile, slot: number, cell: number): void {
    if (slot < 0) return;
    const mesh = this.meshes.get(tile)!;
    const x = cell % this.size;
    const z = (cell - x) / this.size;
    const h = Math.max(this.heights[cell] ?? 1, tile === 'water' ? WATER_LEVEL : 1);
    const height = h * Y_STEP;
    this.dummy.position.set(x, height / 2, z);
    this.dummy.scale.set(1, height, 1);
    this.dummy.rotation.set(0, 0, 0);
    this.dummy.updateMatrix();
    mesh.setMatrixAt(slot, this.dummy.matrix);
    const v = jitterOf(cell);
    this.color.setRGB(v, v, v);
    mesh.setColorAt(slot, this.color);
  }

  private dirty = new Set<Tile>();

  private markDirty(tile: Tile): void {
    this.dirty.add(tile);
  }

  /** Upload whatever changed this tick. Call once after applying a diff. */
  flush(): void {
    if (this.dirty.size === 0) return; // most ticks touch no cells at all
    for (const tile of this.dirty) {
      const mesh = this.meshes.get(tile);
      if (!mesh) continue;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    this.dirty.clear();
  }

  dispose(): void {
    for (const mesh of this.meshes.values()) {
      const mats = mesh.material as THREE.Material[];
      mats.forEach((m) => m.dispose());
      mesh.dispose();
    }
    this.geom.dispose();
    this.meshes.clear();
  }
}

export function disposeGroup(group: THREE.Object3D): void {
  group.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else mat?.dispose();
  });
}
